import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useState, useRef, useMemo, useLayoutEffect, useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { DATE_FORMATS } from "../../../constants/date/date.js";
import { TIME_FORMATS } from "../../../constants/time/time.js";
import { parseISO, format, isBefore, startOfDay, subDays } from "date-fns";
import { withAppointmentDetailsProviders } from "./appointment-details.providers.js";
import { AppointmentModal } from "./appointment-details.styles.js";
import { Language } from "./appointment-details.types.js";
import { AppointmentCancellationDialog } from "./components/appointment-cancellation-dialog/appointment-cancellation-dialog.js";
import { AppointmentDeletionDialog } from "./components/appointment-deletion-dialog/appointment-deletion-dialog.js";
import { AppointmentForm } from "./components/appointment-form/appointment-form.js";
import { parseInitialValues } from "./components/appointment-form/appointment-form.helpers.js";
import { AppointmentInfo } from "./components/appointment-info/appointment-info.js";
import "@mui/material";
import "./components/appointment-info/appointment-info.styles.js";
import { getOpenAvailyBookingFromAppointment } from "./helpers/appointment.js";
import { getInitialPosition } from "./helpers/getInitialPosition.js";
import { getButtonsLabels } from "./helpers/getButtonsLabels.js";
import { usePrevious, getLocale } from "./helpers/common.js";
import { useAppointment } from "./hooks/useAppointment.js";
import { useAppointmentForm } from "./hooks/useAppointmentForm.js";
import { useSaveAppointment } from "./hooks/useSaveAppointment.js";
import "../../../constants/colors/palette.js";
import "../../../constants/create-theme/create-theme.js";
import "@mui/material/styles";
import "../../../themes/shadows/shadows.js";
import "../../../themes/typography/typography.js";
import "../../../themes/typography/utils/defaultVariantMapping/defaultVariantMapping.js";
import "../../../themes/typography/utils/typographyVariants/typographyVariants.js";
import "@tanstack/react-query";
import "../../../themes/alert/alert.js";
import "../../../themes/alert/components/ErrorIcon/ErrorIcon.js";
import "@mui/material/SvgIcon/index.js";
import "../../../themes/alert/components/InfoIcon/InfoIcon.js";
import "../../../themes/alert/components/SuccessIcon/SuccessIcon.js";
import "../../../themes/alert/components/WarningIcon/WarningIcon.js";
import "../../../themes/button/button.js";
import "../../../themes/button/styles/external/bordered.styles.js";
import "color-alpha";
import "../../../themes/button/styles/external/filled.styles.js";
import "../../../themes/button/styles/internal/bordered.styles.js";
import "../../../themes/button/styles/internal/borderless.styles.js";
import "../../../themes/button/styles/internal/filled.styles.js";
import "../../../themes/button/styles/internal/shadowed.styles.js";
import "../../../themes/dialog/dialog.js";
import "../../../themes/external-theme/external-theme.js";
import "../../../constants/breakpoints/breakpoints.js";
import "../../../themes/checkbox/checkbox.js";
import "../../../themes/checkbox/components/CheckedIcon/CheckedIcon.js";
import "../../../themes/checkbox/components/IndeterminateIcon/IndeterminateIcon.js";
import "../../../themes/form-control-label/form-control-label.js";
import "../../../themes/radio/radio.js";
import "../../../themes/switch/switch.js";
import "../../../themes/filled-input/filled-input.js";
import "../../../themes/pickers-popper/pickers-popper.js";
import "../../../themes/popover/popover.js";
import "../../../themes/tooltip/tooltip.js";
import "./providers/axios-provider.js";
import "axios";
import "./providers/appointments-provider.js";
import "./hooks/useAppointments.js";
import "date-fns/locale";
import "lottie-react";
import "../../../components/avatar-group/avatar-group.styles.js";
import "@mui/system";
import "../../../components/date-pickers/pickers-action-bar/pickers-action-bar.js";
import "@mui/material/Button/index.js";
import "@mui/x-date-pickers/internals/index.js";
import "../../../components/date-pickers/pickers-action-bar/pickers-action-bar.styles.js";
import "../../../components/date-pickers/date-picker/theme/index.js";
import "../../../themes/date-calendar/date-calendar.js";
import "../../../themes/date-picker-toolbar/date-picker-toolbar.js";
import "../../../themes/day-calendar/day-calendar.js";
import "../../../themes/form-helper-text/form-helper-text.js";
import "../../../themes/form-label/form-label.js";
import "../../../themes/month-calendar/month-calendar.js";
import "../../../themes/pickers-arrow-switcher/pickers-arrow-switcher.js";
import "../../../themes/pickers-calendar-header/pickers-calendar-header.js";
import "../../../themes/pickers-day/pickers-day.js";
import "@mui/x-date-pickers";
import "../../../themes/pickers-month/pickers-month.js";
import "../../../themes/pickers-toolbar/pickers-toolbar.js";
import "../../../themes/pickers-year/pickers-year.js";
import "../../../themes/year-calendar/year-calendar.js";
import "../../../components/dialog/dialog.js";
import "@mui/icons-material/Close.js";
import "../../../components/dialog/dialog.constants.js";
import "../../../components/dialog/dialog.styles.js";
import "@emotion/react";
import "../../../components/dialog/DraggablePaperComponent.js";
import "react-draggable";
import "../../../components/icons/all/all.js";
import "../../../components/icons/CalendarEventIcon/CalendarEventIcon.js";
import "../../../components/icons/MainIcon/MainIcon.js";
import "../../../components/icons/CalendarIcon/CalendarIcon.js";
import "../../../components/icons/CardIcon/CardIcon.js";
import "../../../components/icons/ChecklistIcon/ChecklistIcon.js";
import "../../../components/icons/ClockIcon/ClockIcon.js";
import "../../../components/icons/CloseIcon/CloseIcon.js";
import "../../../components/icons/EditIcon/EditIcon.js";
import "../../../components/icons/EmptyCalendar/EmptyCalendar.js";
import "../../../components/icons/EventNote/EventNote.js";
import "../../../components/icons/FilterIcon/FilterIcon.js";
import "../../../components/icons/ListIcon/ListIcon.js";
import "../../../components/icons/MessageBubble/MessageBubble.js";
import "../../../components/icons/PhoneIcon/PhoneIcon.js";
import "../../../components/icons/PinIcon/PinIcon.js";
import "../../../components/icons/SearchIcon/SearchIcon.js";
import "../../../components/icons/TherapyNotesIcon/TherapyNotesIcon.js";
import "../../../components/icons/TimerIcon/TimerIcon.js";
import "../../../components/icons/TreatmentChairIcon/TreatmentIcon.js";
import "../../../components/icons/VisibilityOffIcon/VisibilityOffIcon.js";
import "../../../components/icons/WarningIcon/WarningIcon.js";
import "../../../components/label/label.styles.js";
import "../../../components/menu-item/menu-item.js";
import "@mui/material/MenuItem/index.js";
import "../../../components/menu-item/components/menu-item-counter/menu-item-counter.js";
import "../../../components/menu-item/assets/icons/minus-circle.js";
import "../../../components/menu-item/assets/icons/plus-circle.js";
import "../../../components/menu-item/components/menu-item-counter/menu-item-counter.styles.js";
import "../../../components/menu-item/menu-item.styles.js";
import "../../../components/avatar/avatar.js";
import "@mui/material/Avatar/index.js";
import "../../../components/avatar/avatar.styles.js";
import "../../../components/slider/slider.styles.js";
import "../../../components/snackbar/snackbar.styles.js";
import "@mui/material/Snackbar/index.js";
import "../../../components/snackbar/snackbar.types.js";
import "../../../components/styled-text-field/styled-text-field.styles.js";
import "../../../components/text-field/text-field.js";
import "@mui/material/TextField/index.js";
import "../../../components/text-field/text-field.styles.js";
import "../../../components/swipeable-drawer/swipeable-drawer.js";
import "../../../components/swipeable-drawer/swipeable-drawer.styles.js";
import "../../../components/translations/utils.js";
import "react-i18next";
import "../../../type/Translations.js";
import "contentful";
import "i18next";
import "lodash/set.js";
import "remarkable";
import "remarkable-react";
import "../../../components/time-select/time-select.styles.js";
import "@mui/icons-material/Timer.js";
import "@mui/material/FormControl/index.js";
import "@mui/material/InputLabel/index.js";
import "@mui/material/Select/index.js";
import "../../../components/time-interval-select/time-interval-select.styles.js";
import "@mui/material/Box/index.js";
import "@hookform/resolvers/yup";
import "yup";
import "./assets/icons/CancellationReason.js";
import "./assets/icons/Comment.js";
import "./hooks/useCancelAppointment.js";
import "./components/appointment-form/appointment-form.constants.js";
import "./components/appointment-cancellation-dialog/appointment-cancellation-dialog.styles.js";
import "./hooks/useDeleteAppointment.js";
import "./components/appointment-deletion-dialog/appointment-deletion-dialog.styles.js";
import "./assets/icons/Duration.js";
import "./assets/icons/Title.js";
import "./hooks/useDoesAppointmentExist.js";
import "./hooks/useTreatments.js";
import "./components/appointment-date-select/appointment-date-select.js";
import "../../components/appointments-picker/appointments-picker.js";
import "../../../components/loader/loader.js";
import "../../../components/loader/data.js";
import "../../../components/animation/animation.js";
import "../../../components/loader/loader.styles.js";
import "date-fns-tz";
import "lodash";
import "swiper/react";
import "../../components/appointments-picker/appointments-picker.helpers.js";
import "date-fns/locale/de";
import "date-fns/locale/en-US";
import "../../components/appointments-picker/appointments-picker.styles.js";
import "@mui/icons-material/ArrowBackIos.js";
import "@mui/icons-material/ExpandMore.js";
import "@mui/material/Card/index.js";
import "@mui/material/CardContent/index.js";
import "../../components/appointments-picker/components/AvailableSlot/AvailableSlot.js";
import "clsx";
import "../../components/appointments-picker/constants/dataTestIds.js";
import "../../components/appointments-picker/components/AvailableSlot/AvailableSlot.styles.js";
import "../../components/appointments-picker/components/CurrentPeriod/CurrentPeriod.js";
import "../../components/appointments-picker/components/CurrentPeriod/CurrentPeriod.styles.js";
import "../../components/appointments-picker/components/NextPeriod/NextPeriod.js";
import "../../components/appointments-picker/components/NextPeriod/NextPeriod.styles.js";
import "@mui/icons-material/KeyboardArrowRight.js";
import "../../components/appointments-picker/components/PrevPeriod/PrevPeriod.js";
import "../../components/appointments-picker/components/PrevPeriod/PrevPeriod.styles.js";
import "@mui/icons-material/KeyboardArrowLeft.js";
import "../../components/appointments-picker/components/QuickFilter/QuickFilter.js";
import "../../components/appointments-picker/components/QuickFilter/QuickFilter.styles.js";
import "../../components/appointments-picker/components/TimeFrames/TimeFrames.js";
import "@mui/icons-material/Check.js";
import "../../components/appointments-picker/components/TimeFrames/TimeFrames.styles.js";
import "../../components/appointments-picker/components/UnavailableSlot/UnavailableSlot.js";
import "../../components/appointments-picker/components/UnavailableSlot/UnavailableSlot.styles.js";
import "../../components/appointments-picker/components/WorkingDay/WorkingDay.js";
import "../../components/appointments-picker/components/WorkingDay/WorkingDay.styles.js";
import "../../components/chemistry-info/chemistry-info.styles.js";
import "../../components/clinic-location-details/clinic-location-details.styles.js";
import "../../components/clinic-location-details/Day/styles.js";
import "../../components/clinic-location-details/Map/Map.js";
import "google-map-react";
import "../../components/floating-button/floating-button-container/styles.js";
import "../../components/floating-button/floating-button.styles.js";
import "../../components/news-section/news-section.styles.js";
import "../../components/obfuscated-text/obfuscated-text.styles.js";
import "../../components/pain-details-form/pain-details-form.styles.js";
import "../../components/pain-details-form/constants/constants.js";
import "../../components/pain-details-form/types.js";
import "react-slick";
import "../../components/task-form/components/Avatar/Avatar.styles.js";
import "../../components/task-form/assets/icons/Avatar.js";
import "../../components/task-form/assets/icons/Logo.js";
import "../../components/task-form/components/Comments/Comments.styles.js";
import "../../components/task-form/assets/icons/Comment.js";
import "lodash/debounce.js";
import "../../components/task-form/components/DatePicker/DatePicker.styles.js";
import "../../components/task-form/components/EmployeeMenuItem/EmployeeMenuItem.styles.js";
import "../../components/task-form/components/EmployeesDropdown/EmployeesDropdown.styles.js";
import "../../components/task-form/components/PatientAutocomplete/PatientAutocomplete.styles.js";
import "../../components/task-form/task-form.entities.js";
import "../../components/task-form/task-form.helpers.js";
import "../../components/task-form/task-form.types.js";
import "../../components/task-form/task-form.styles.js";
import "../../components/task-form/assets/icons/Important.js";
import "../../components/task-form/assets/icons/TaskCategory.js";
import "../../components/task-form/assets/icons/TaskDescription.js";
import "../../components/task-form/assets/icons/TaskName.js";
import "../../components/task-form/theme/index.js";
import "../../components/task-form/theme/overrides/Button.js";
import "../../components/task-form/theme/overrides/PickersLayout.js";
import "../../components/task-form/theme/overrides/Menu.js";
import "../../components/task-form/theme/overrides/Autocomplete.js";
import "../../components/team-section/constants/constants.js";
import "../../components/team-section/components/Rating/styles.js";
import "../../components/team-section/team-section.styles.js";
import "../../components/testimonials-section/constants.js";
import "../../components/testimonials-section/PractitionerTestimonialCard/styles.js";
import "../../components/testimonials-video-section/testimonials-video-section.styles.js";
import "../../components/employee-select/employee-select.js";
import "../../../themes/icon-button/icon-button.js";
import "../../../themes/tabs/tabs.js";
import "../../components/employee-select/components/employee-search-input/employee-search-input.js";
import "../../components/employee-select/components/employee-search-input/employee-search-input.styles.js";
import "../../components/employee-select/assets/icons/Search.js";
import "../../components/employee-select/employee-select.styles.js";
import "../../components/employee-select/assets/icons/Arrow.js";
import "../../components/employee-select/assets/icons/Employees.js";
import "../../components/ana/utils/shared/Checkbox.js";
import "../../components/ana/utils/shared/FormControlLabel.js";
import "../../components/ana/components/translate/translate.js";
import "../../components/ana/components/checklist-input/checklist-input.styles.js";
import "react-dom";
import "../../components/ana/components/address-field/address-field.js";
import "formik";
import "../../components/ana/utils/helpers/getHasError.js";
import "../../components/ana/utils/hooks/useAddressSuggestions.js";
import "use-debounce";
import "../../components/ana/utils/helpers/googlePlaces.js";
import "../../components/ana/utils/helpers/places.js";
import "../../components/ana/utils/hooks/useFormHelpers.js";
import "../../components/ana/utils/hooks/useSubmitCount.js";
import "../../components/ana/utils/shared/TextField.js";
import "../../components/ana/utils/shared/TextFieldLabel.js";
import "../../components/ana/utils/yup/main.js";
import "../../components/ana/utils/fields/personalInformation.js";
import "../../../type/Appointment.js";
import "../../components/ana/components/masked-date-input/masked-date-input.js";
import "react-text-mask";
import "../../components/ana/utils/entities/Form.js";
import "../../components/ana-toolbox/ana-toolbox.js";
import "date-fns/fp/format";
import "date-fns/isValid";
import "date-fns/parse";
import "../../components/ana/utils/helpers/toBoolean.js";
import "../../components/ana/utils/helpers/createFields.js";
import "../../components/ana/utils/helpers/resetFields.js";
import "../../components/ana/utils/yup/yup.js";
import "../../../utils/pipe.js";
import "date-fns/fp";
import "libphonenumber-js/max";
import "libphonenumber-js/mobile";
import "../../components/ana/utils/helpers/predicates.js";
import "../../components/ana/utils/yup/emailValidation.js";
import "../../components/ana/utils/fields/contactDetails.js";
import "../../components/ana/utils/helpers/featureFlags.js";
import "../../components/ana/utils/helpers/getAgeFromBirthday.js";
import "../../components/ana/utils/yup/buildValidationSchema.js";
import "../../components/ana/utils/fields/miscellaneous.js";
import "../../components/ana/utils/fields/insurance.js";
import "../../components/ana/utils/fields/allergiesAndMedications.js";
import "../../../type/Disease.js";
import "../../components/ana/utils/config/medicaments.js";
import "../../components/ana/utils/fields/wishesAndExpectations.js";
import "../../components/ana/utils/fields/consents.js";
import "../../components/ana/utils/fields/profileInterest.js";
import "../../components/ana/components/field-helper-text/field-helper-text.js";
import "../../components/ana/components/checkbox-field/checkbox-field.styles.js";
import "../../components/ana/components/date-picker-field/date-picker-field.js";
import "@mui/x-date-pickers/StaticDatePicker/index.js";
import "date-fns/fp/formatWithOptions";
import "../../components/ana/utils/helpers/date.js";
import "date-fns/fp/startOfMonth";
import "date-fns/fp/subYears";
import "../../components/ana/components/date-picker-field/date-picker-field.styles.js";
import "../../components/ana/components/phone-field/phone-field.js";
import "react-window";
import "@mui/icons-material/ArrowDropDown.js";
import "libphonenumber-js";
import "../../components/ana/utils/helpers/phoneMetadata.js";
import "../../components/ana/components/text-input/text-input.js";
import "../../components/ana/components/text-input/text-input.styles.js";
import "../../components/ana/components/phone-field/phone-field.styles.js";
import "../../components/ana/components/radio-group/radio-group.js";
import "use-deep-compare";
import "../../components/ana/utils/shared/Radio.js";
import "../../components/ana/utils/shared/RadioFormControl.js";
import "../../components/ana/utils/shared/RadioFormControlLabel.js";
import "../../components/ana/utils/shared/RadioFormLabel.js";
import "../../components/ana/utils/shared/RadioGroup.js";
import "../../components/ana/components/select-input/select-input.styles.js";
import "../../components/ana/components/toggle-field/toggle-field.js";
import "../../components/ana/utils/shared/ToggleContainer.js";
import "../../components/ana/utils/shared/ToggleFormControlLabel.js";
import "../../components/ana/utils/hooks/useFormValue.js";
import "../../components/ana/components/form-layout/form-layout.styles.js";
import "../../components/ana/utils/shared/Button.js";
import "../../components/ana/components/submit-button/submit-button.styles.js";
import "../../components/ana/components/toggle-multi-checkbox/toggle-multi-checkbox.styles.js";
import "../../components/ana/utils/config/diseases.js";
import "../../components/ana/components/insurance-field/insurance-field.js";
import "fuse.js";
import "../../components/ana/components/insurance-field/insurance-field-helpers.js";
import "../../components/ana/components/insurance-field/Insurance-field.styles.js";
import "../../components/ana/utils/shared/SectionTitle.js";
import "../../components/ana/components/callout/Callout.styles.js";
import "../../components/ana/utils/types/contentful.js";
import "../../components/ana/utils/shared/AddressLayoutItem.js";
import "../../components/ana/utils/helpers/getIsRequired.js";
import "../../components/ana/forms/miscellaneous/miscellaneous.js";
import "../../components/ana/components/form-layout/form-layout.js";
import "../../components/ana/utils/helpers/createField.js";
import "../../components/ana/components/checkbox-field/checkbox-field.js";
import "../../components/ana/components/select-field/select-field.js";
import "../../components/ana/components/select-input/select-input.js";
import "../../components/ana/components/text-field/text-field.js";
import "../../components/ana/components/submit-button/submit-button.js";
import "../../components/ana/utils/helpers/getFormInitialErrors.js";
import "../../components/ana/utils/config/wishes.js";
import "../../components/ana/forms/preconditions/preconditions.js";
import "../../components/ana/components/disease-group-field/disease-group-field.js";
import "../../components/ana/utils/helpers/DiseaseType.js";
import "../../components/ana/components/disease-field/disease-field.js";
import "../../components/ana/components/tag/tag.js";
import "../../components/ana/components/tag/tag.styles.js";
import "@mui/icons-material/Cancel.js";
import "../../components/ana/components/disease-field/disease-field.styles.js";
import "@mui/styles";
import "../../components/ana/components/disease-group-field/disease-group-field.helpers.js";
import "../../components/ana/components/disease-group-field/disease-group-field.styles.js";
import "../../components/ana/utils/hooks/usePreConditionsToggle.js";
import "../../components/ana/utils/helpers/scrollToElementIfNotVisible.js";
import "../../components/ana/utils/helpers/isInViewport.js";
import "../../components/ana/forms/preconditions/preconditions.styles.js";
import "../../components/ana/components/legal-representative-dialog/legal-representative-dialog.styles.js";
import "../../components/ana/utils/shared/Modal.js";
import "../../components/ana/utils/helpers/i18n.js";
import "../../components/ana/components/ana-dialog/ana-dialog.styles.js";
import "../../components/ana/utils/config/wishesAndExpectations.js";
import "../../../hooks/useFilterEmployees/useFilterEmployees.js";
import "../../../type/Employee.js";
import "../../../utils/array.js";
import "fast-memoize";
import "../../../utils/employee/getUniqueByRoles.js";
import "lodash/reverse.js";
import "lodash/uniqBy.js";
import "../../components/website/common/button/styles.js";
import "../../components/website/jobs/job-description-section/styles.js";
import "../../components/website/jobs/job-details-hero-section/styles.js";
import "../../components/website/common/button/button.js";
import "../../../hooks/useResponsive.js";
import "../../components/smileview-section/hooks/useWindowSize.js";
import "../../components/website/directions-section/styles.js";
import "react-headroom";
import "../../components/page-header/context/header-context.js";
import "../../components/page-header/components/header-skip/styles.js";
import "../../../components/expandable/expandable-dropdown/expandable-dropdown.styles.js";
import "../../../components/expandable/expandable-group/expandable-group-context.js";
import "../../components/page-header/page-header-mobile/page-header-mobile-content/styles.js";
import "query-string";
import "../../components/authentication/components/CredentialsProvider.js";
import "../../components/authentication/config.js";
import "../../components/authentication/helpers/time.js";
import "../../components/authentication/hooks/useStickyState.js";
import "../../components/authentication/services/api.js";
import "../../components/authentication/helpers/credentials.js";
import "../../components/authentication/helpers/headers.js";
import "../../components/authentication/components/RouterProvider.js";
import "../../components/authentication/components/SecurityConfigProvider.js";
import "../../components/authentication/services/i18n.js";
import "../../components/authentication/components/auth-container/auth-container.styles.js";
import "@emotion/styled";
import "../../components/authentication/components/login-page/login-page.styles.js";
import "../../components/authentication/components/TrustedDeviceProvider.js";
import "../../components/authentication/components/HistoryProvider.js";
import "./assets/icons/Date.js";
import "./hooks/useDebounce.js";
import "./hooks/useTimeSlots.js";
import "./components/appointment-date-select/appointment-date-select.styles.js";
import "./components/employee-autocomplete/employee-autocomplete.js";
import "@mui/icons-material";
import "./assets/icons/Employee.js";
import "./hooks/useEmployeeSearch.js";
import "./components/employee-autocomplete/employee-autocomplete.skeleton.js";
import "./components/employee-autocomplete/employee-autocomplete.styles.js";
import "./components/employee-select/employee-select.js";
import "../../../components/chip/chip.js";
import "@mui/material/Chip/index.js";
import "../../../components/chip/chip.styles.js";
import "./assets/icons/Cross.js";
import "./components/employee-select/employee-select.skeleton.js";
import "./components/employee-select/employee-select.styles.js";
import "./components/new-patient-form/new-patient-form.js";
import "../../../components/date-pickers/date-picker/date-picker.js";
import "@mui/x-date-pickers/AdapterDateFns/index.js";
import "@mui/x-date-pickers/DatePicker/index.js";
import "./assets/icons/Birthday.js";
import "./assets/icons/Insurance.js";
import "./assets/icons/Mail.js";
import "./assets/icons/Patient.js";
import "./assets/icons/Phone.js";
import "./components/new-patient-form/new-patient-form.styles.js";
import "./components/patient-autocomplete/patient-autocomplete.js";
import "./hooks/usePatientSearch.js";
import "./components/patient-autocomplete/patient-autocomplete.skeleton.js";
import "./components/patient-autocomplete/patient-autocomplete.styles.js";
import "./components/treatment-autocomplete/treatment-autocomplete.js";
import "./assets/icons/Treatment.js";
import "./components/treatment-autocomplete/treatment-autocomplete.helpers.js";
import "./components/treatment-autocomplete/treatment-autocomplete.skeleton.js";
import "./components/treatment-autocomplete/treatment-autocomplete.styles.js";
import "./components/appointment-form/appointment-form.styles.js";
import "../../components/obfuscated-text/obfuscated-text.js";
import "./assets/icons/CancellationType.js";
import "./assets/icons/CancellationUser.js";
import "./assets/icons/CreatedBy.js";
import "./assets/icons/WarningIcon.js";
import "./components/appointment-info/appointment-info.skeleton.js";
import "../../../components/icon/icon.js";
const AppointmentDetailsComponent = ({
  anchorEl,
  onClose,
  onCancel,
  onDelete,
  onSave,
  onPatientClick,
  onSchedule,
  appointmentReferenceId,
  initialValues,
  clinicReferenceId,
  auth,
  echoCoreBaseURL,
  language = Language.ENGLISH,
  employees = [],
  labels,
  draggable,
  day,
  isOpenAppointment,
  showInitialForm = false,
  isObfuscated,
  // TODO: Remove this flag once the scheduler is fixed in Claire
  allowSchedule = true
}) => {
  var _a, _b, _c, _d;
  const [isNewPatient, setIsNewPatient] = useState(!!(initialValues == null ? void 0 : initialValues.patient) && !((_a = initialValues == null ? void 0 : initialValues.patient) == null ? void 0 : _a.referenceId));
  const [showForm, setShowForm] = useState(!appointmentReferenceId || showInitialForm);
  const [showCancellationDialog, setShowCancellationDialog] = useState(false);
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  const [initialPosition, setInitialPosition] = useState(void 0);
  const [isOverbooked, setIsOverbooked] = useState(false);
  const modalRef = useRef(null);
  const {
    data: appointment,
    queryResult: { isFetching, remove }
  } = useAppointment({
    appointmentReferenceId,
    auth,
    baseURL: echoCoreBaseURL,
    isOpenAppointment
  });
  const previousModalHeight = usePrevious((_b = modalRef.current) == null ? void 0 : _b.offsetHeight);
  const previousIsFetching = usePrevious(isFetching);
  const previousShowForm = usePrevious(showForm);
  const previousIsOverbooked = usePrevious(isOverbooked);
  const formInitialValues = useMemo(
    () => appointmentReferenceId && !showInitialForm ? appointment : initialValues,
    [appointment, initialValues, appointmentReferenceId, showInitialForm]
  );
  const { form } = useAppointmentForm({
    initialValues: parseInitialValues(formInitialValues),
    date: day,
    isNewPatient,
    isOpenAppointment,
    appointmentReferenceId
  });
  const {
    formState: { errors }
  } = form;
  const { mutateAsync: saveAppointment, isLoading: isSaving } = useSaveAppointment({
    clinicReferenceId,
    form,
    isOpenAppointment
  });
  const start = useMemo(
    () => appointment && "startTime" in appointment && appointment.startTime ? parseISO(appointment.startTime) : null,
    [appointment]
  );
  const end = useMemo(
    () => appointment && "endTime" in appointment && appointment.endTime ? parseISO(appointment.endTime) : null,
    [appointment]
  );
  const employee = useMemo(
    () => appointment && "employee" in appointment ? appointment == null ? void 0 : appointment.employee : employees.find(
      ({ referenceId }) => appointment && "employeeReferenceId" in appointment && referenceId === (appointment == null ? void 0 : appointment.employeeReferenceId)
    ),
    [appointment, employees]
  );
  const title = useMemo(() => {
    if (!showForm)
      return (appointment == null ? void 0 : appointment.title) || " ";
    if (appointmentReferenceId)
      return `${isOpenAppointment ? labels.title.editOpenAppointment : labels.title.edit}${isNewPatient ? ` - ${labels.title.createPatient}` : ""}`;
    return `${isOpenAppointment ? labels.title.createOpenAppointment : labels.title.create}${isNewPatient ? ` - ${labels.title.createPatient}` : ""}`;
  }, [showForm, appointment, labels, isNewPatient, appointmentReferenceId, isOpenAppointment]);
  const subtitle = useMemo(
    () => (appointment == null ? void 0 : appointment.referenceId) && start && end && !showForm ? `${format(start, "EEEE", { locale: getLocale(language) })}, ${format(
      end,
      DATE_FORMATS.DAY_MONTH_YEAR
    )}, ${format(start, TIME_FORMATS.SHORT)} - ${format(end, TIME_FORMATS.SHORT)}` : "",
    [start, end, language, showForm, appointment]
  );
  const dialogLabels = useMemo(
    () => getButtonsLabels(appointment, showForm, labels),
    [showForm, appointment, labels]
  );
  const handleCancelButtonClick = (isSaved) => {
    if (isOpenAppointment && !showForm)
      return setShowDeletionDialog(true);
    const values = showForm && !isSaved ? form.getValues() : void 0;
    if (!appointmentReferenceId)
      return onClose(void 0, void 0, values);
    if (!showForm)
      return setShowCancellationDialog(true);
    setShowForm(false);
    setIsNewPatient(false);
    form.reset(void 0, { keepDirty: false, keepErrors: false, keepIsValid: false });
    return null;
  };
  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick")
      return;
    const values = showForm ? form.getValues() : void 0;
    onClose == null ? void 0 : onClose(event, reason, values);
  };
  const handleFormSubmit = async (data) => {
    try {
      await saveAppointment({ data });
      handleCancelButtonClick(true);
      onSave == null ? void 0 : onSave();
    } catch (e) {
    }
  };
  const handleSaveButtonClick = () => {
    if (isOpenAppointment && !!appointment)
      return onSchedule == null ? void 0 : onSchedule(appointment);
    if (!appointment || "cancellationType" in appointment && !(appointment == null ? void 0 : appointment.cancellationType))
      return showForm ? form.handleSubmit(handleFormSubmit)() : setShowForm(true);
    setShowDeletionDialog(true);
    return null;
  };
  const handlePatientClick = () => {
    var _a2;
    onPatientClick == null ? void 0 : onPatientClick((_a2 = appointment == null ? void 0 : appointment.patient) == null ? void 0 : _a2.referenceId);
  };
  const handlelDialogCancelButtonClick = () => {
    setShowCancellationDialog(false);
    setShowDeletionDialog(false);
  };
  const handleDialogCancelButtonClick = () => {
    handlelDialogCancelButtonClick();
    onCancel == null ? void 0 : onCancel();
  };
  const handleDialogDeleteButtonClick = () => {
    handlelDialogCancelButtonClick();
    onDelete == null ? void 0 : onDelete();
  };
  const disableSave = showForm && !!Object.keys(errors).length;
  const disableEditAppointment = appointment && "endTime" in appointment && appointment.endTime && isBefore(parseISO(appointment.endTime), startOfDay(subDays(/* @__PURE__ */ new Date(), 1)));
  const isOpenForAvailyBooking = appointment && "blocking" in appointment && getOpenAvailyBookingFromAppointment(appointment.blocking);
  const hints = useMemo(() => {
    var _a2;
    const qaKeys = Object.values(((_a2 = appointment == null ? void 0 : appointment.additionalAppointmentHints) == null ? void 0 : _a2.hints) ?? {}).flat();
    return qaKeys.reduce(
      (acc, pain) => `${acc}
${labels.pain.questions[pain.questionContentfulKey]}: ${labels.pain.answers[pain.answerContentfulKey]}`,
      ""
    );
  }, [(_c = appointment == null ? void 0 : appointment.additionalAppointmentHints) == null ? void 0 : _c.hints, labels.pain]);
  useLayoutEffect(() => {
    const prevModalHeightNotSet = !previousModalHeight && !!modalRef.current;
    const initialIsFetching = previousIsFetching && !isFetching;
    const isEditMode = !previousShowForm && showForm;
    const hasWarningMessage = !previousIsOverbooked && isOverbooked;
    const canUpdateInitialPosition = prevModalHeightNotSet || initialIsFetching || isEditMode || hasWarningMessage;
    if (canUpdateInitialPosition) {
      setInitialPosition(draggable ? getInitialPosition(anchorEl, modalRef) : void 0);
    }
  }, [
    previousModalHeight,
    isFetching,
    anchorEl,
    showForm,
    previousIsFetching,
    previousShowForm,
    draggable,
    previousIsOverbooked,
    isOverbooked
  ]);
  useLayoutEffect(() => {
    var _a2;
    if (isNewPatient) {
      const currentHeight = ((_a2 = modalRef.current) == null ? void 0 : _a2.offsetHeight) ?? 0;
      const previousHeight = previousModalHeight ?? 0;
      if (currentHeight - previousHeight > 48) {
        const initialPosition2 = draggable ? getInitialPosition(anchorEl, modalRef) : void 0;
        initialPosition2 && setInitialPosition({
          x: initialPosition2.x,
          y: initialPosition2.y - 32
          // Add some offset to avoid the modal being cut off when required field message is shown
        });
      }
    }
  }, [anchorEl, isNewPatient, draggable, previousModalHeight, (_d = modalRef.current) == null ? void 0 : _d.offsetHeight, isOverbooked]);
  useEffect(() => {
    if (!showForm && previousShowForm)
      remove();
  }, [showForm]);
  useEffect(() => {
    setShowForm(!appointmentReferenceId || showInitialForm);
  }, [appointmentReferenceId]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showCancellationDialog && /* @__PURE__ */ jsx(
      AppointmentCancellationDialog,
      {
        appointment,
        labels,
        onCancel: handlelDialogCancelButtonClick,
        onSave: handleDialogCancelButtonClick
      }
    ),
    showDeletionDialog && /* @__PURE__ */ jsx(
      AppointmentDeletionDialog,
      {
        appointment,
        labels: labels.deletionDialog,
        onCancel: handlelDialogCancelButtonClick,
        onSave: handleDialogDeleteButtonClick,
        isOpenAppointment
      }
    ),
    /* @__PURE__ */ jsx(FormProvider, { ...form, children: /* @__PURE__ */ jsxs(
      AppointmentModal,
      {
        anchorEl,
        variant: isOpenForAvailyBooking ? "warning" : "default",
        isBlocking: !!isOpenForAvailyBooking,
        open: true,
        title,
        subtitle,
        onClose: handleDialogClose,
        labels: dialogLabels,
        disabled: isSaving || isFetching && !!appointmentReferenceId,
        disableSave,
        disableClose: showDeletionDialog || showCancellationDialog,
        width: anchorEl ? 480 : 680,
        draggable,
        initialPosition,
        ref: modalRef,
        ...!disableEditAppointment && {
          // TODO: Remove this condition once the scheduler is fixed in Claire
          onSave: allowSchedule ? handleSaveButtonClick : void 0
        },
        ...appointment && "cancellationType" in appointment && !!appointment.cancellationType && {
          variant: "grey"
        },
        ...appointment && "cancellationType" in appointment && !appointment.cancellationType && {
          onCancel: handleCancelButtonClick
        },
        children: [
          !showForm && /* @__PURE__ */ jsx(
            AppointmentInfo,
            {
              isLoading: isFetching,
              labels,
              appointment,
              employee,
              onPatientClick: handlePatientClick,
              isOpenAppointment,
              isObfuscated,
              hints
            }
          ),
          showForm && /* @__PURE__ */ jsx(
            AppointmentForm,
            {
              appointment,
              clinicReferenceId,
              appointmentReferenceId,
              labels,
              language,
              day,
              onShowNewPatient: () => setIsNewPatient(true),
              isNewPatient,
              employees,
              isWide: !anchorEl,
              hasInitialValues: !!anchorEl,
              isOpenAppointment,
              auth: auth ?? "",
              setIsOverbooked,
              hints
            }
          )
        ]
      }
    ) })
  ] });
};
const AppointmentDetails = withAppointmentDetailsProviders(AppointmentDetailsComponent);
export {
  AppointmentDetails,
  AppointmentDetailsComponent
};
